/** ***********************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 ************************************************************************* */

import React from 'react';
import Button from '@react/react-spectrum/Button';
import Switch from '@react/react-spectrum/Switch';
import Checkbox from '@react/react-spectrum/Checkbox';
import AlertMedium from '@react/react-spectrum/Icon/core/AlertMedium';
import { observer } from 'mobx-react';
import { AppDialogInputLabel } from '../../stores/dialog';
import { APP_DIALOG } from '../../utils/enums';
import RadioWithCheckBoxes from './radioWithCheckBoxes/radioWithCheckBoxes';

import './dialog.scss';


const VARIANT_ICONS = {
    error: AlertMedium,
};

const { SWITCH, CHECKBOX, RADIO_WITH_CHECKBOXES } = APP_DIALOG.INPUT_TYPE;
const renderInputs = inputList => (
    inputList.map((input) => {
        if (input instanceof AppDialogInputLabel) {
            return <div key="input-label" className="input-label">{input.label}</div>;
        }
        const {
            type,
            disabled,
            localizedText,
            ariaLabel,
            value,
            updateValue,
            additionalData,
        } = input;
        switch (type) {
        case SWITCH:
            return (
                <Switch
                    disabled={disabled}
                    key={localizedText}
                    defaultChecked={value}
                    label={localizedText}
                    ariaLabel={ariaLabel}
                    onChange={updateValue} />
            );
        case CHECKBOX:
            return (
                <Checkbox
                    disabled={disabled}
                    key={localizedText}
                    defaultChecked={value}
                    label={localizedText}
                    ariaLabel={ariaLabel}
                    onChange={updateValue} />
            );
        case RADIO_WITH_CHECKBOXES:
            return (
                <RadioWithCheckBoxes
                    disabled={disabled}
                    key={localizedText}
                    defaultChecked={value}
                    label={localizedText}
                    ariaLabel={ariaLabel}
                    onChange={updateValue}
                    checkBoxList={additionalData}
                />
            );
        default:
            return <div />;
        }
    })
);

const renderActions = (actionList, type, onAction) => (
    actionList.map(({
        actionType, localizedText, actionId, ariaLabel,
    }) => (
        <Button
            className="app-dialog-button"
            onClick={() => onAction(actionId, type)}
            quiet={actionType.quiet}
            variant={actionType.variant}
            label={localizedText}
            aria-label={ariaLabel || localizedText}
            key={actionId} />
    ))
);

const renderDescription = descriptionList => (
    descriptionList.map(({ type, localizedText }) => (
        <div
            key={localizedText}
            className={`app-dialog-description app-dialog-description-${type}`}>
            {type === APP_DIALOG.DESCRIPTION_TYPE.LIST ? <ul className="description-list-wrapper"><li>{localizedText}</li></ul> : localizedText}
        </div>
    ))
);


export default observer(({ appDialog, variant }) => {
    const {
        type,
        descriptionList,
        actionList,
        inputList,
        onAction,
        heading,
    } = appDialog;
    const Icon = VARIANT_ICONS[variant];
    return (
        <div className="app-dialog-wrapper">
            <div className={`app-dialog-header app-dialog-${variant}`}>
                {heading}
                {Icon && <div><Icon /></div>}
            </div>
            <div className="description-wrapper">
                {renderDescription(descriptionList)}
            </div>
            {inputList.length ? (
                <div className="app-dialog-inputs">
                    {renderInputs(inputList)}
                </div>
            ) : []}
            {actionList.length ? (
                <div className="app-dialog-actions">
                    {renderActions(actionList, type, onAction)}
                </div>
            ) : []}
        </div>
    );
});
